import {
  ChangeTaskStatusRequest,
  ListTasksQuery,
  ListTasksResponse,
  listTasksResponseSchema,
  Task,
  taskPath,
  taskSchema,
  taskStatusPath,
  TASKS_PATH,
  UpdateTaskRequest,
} from '@quizdb/common';
import { quizdbApi } from '../api/quizdbApi';
import { problemCode } from '../api/problemCode';

/** The task list's filters (QZ-D8); the cursor is paging, not a filter. */
export type TaskListFilters = Omit<ListTasksQuery, 'cursor'>;

/** The task bank's endpoints (QZ-D8). */
export const taskApi = quizdbApi.injectEndpoints({
  endpoints: (builder) => ({
    listTasks: builder.infiniteQuery<ListTasksResponse, TaskListFilters, string | undefined>({
      infiniteQueryOptions: {
        initialPageParam: undefined,
        getNextPageParam: (last) => last.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        url: TASKS_PATH,
        params: Object.fromEntries(
          Object.entries({ ...queryArg, cursor: pageParam }).filter(([, v]) => v !== undefined)
        ),
      }),
      transformResponse: (response: unknown) => listTasksResponseSchema.parse(response),
      providesTags: ['TaskList'],
    }),
    /**
     * The first page of the task list, fetched on demand to find the next draft to review. Kept
     * apart from `listTasks` so it neither refetches nor shares the list page's paged cache.
     */
    listTasksFirstPage: builder.query<ListTasksResponse, TaskListFilters>({
      query: (filters) => ({
        url: TASKS_PATH,
        params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined)),
      }),
      transformResponse: (response: unknown) => listTasksResponseSchema.parse(response),
      providesTags: ['TaskList'],
      keepUnusedDataFor: 0,
    }),
    getTask: builder.query<Task, string>({
      query: (taskId) => taskPath(taskId),
      transformResponse: (response: unknown) => taskSchema.parse(response),
      providesTags: (_result, _error, taskId) => [{ type: 'Task', id: taskId }],
    }),
    updateTask: builder.mutation<Task, { taskId: string; request: UpdateTaskRequest }>({
      query: ({ taskId, request }) => ({ url: taskPath(taskId), method: 'PUT', body: request }),
      transformResponse: (response: unknown) => taskSchema.parse(response),
      // QZ-D41: edits carry the updatedAt they were made against; on a TaskChanged conflict the
      // reviewer keeps their edits and reloads deliberately, instead of an invalidation wiping
      // them out from under the still-open form.
      invalidatesTags: (_result, error, { taskId }) =>
        problemCode(error) === 'TaskChanged' ? [] : ['TaskList', { type: 'Task', id: taskId }],
    }),
    changeTaskStatus: builder.mutation<Task, { taskId: string; request: ChangeTaskStatusRequest }>({
      query: ({ taskId, request }) => ({
        url: taskStatusPath(taskId),
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: unknown) => taskSchema.parse(response),
      // QZ-D41: see updateTask above — a TaskChanged conflict on a status change must not
      // invalidate and refetch either, or the reviewer's unsaved edits are lost the same way.
      // Otherwise, approving or reopening moves an approved count (QZ-D104).
      invalidatesTags: (_result, error, { taskId }) =>
        problemCode(error) === 'TaskChanged'
          ? []
          : ['TaskList', { type: 'Task', id: taskId }, 'DocumentList'],
    }),
  }),
});

export const {
  useListTasksInfiniteQuery,
  useLazyListTasksFirstPageQuery,
  useGetTaskQuery,
  useUpdateTaskMutation,
  useChangeTaskStatusMutation,
} = taskApi;
